import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Divider,
  Chip,
  Grid
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { cryptoPriceService, CryptoCurrency } from '../../services/cryptoPriceService';
import { useAuth } from '../../contexts/AuthContext';

export const CryptoWatchlist: React.FC = () => {
  const { state } = useAuth();
  const [watchlist, setWatchlist] = useState<string[]>([]);
  const [cryptoData, setCryptoData] = useState<CryptoCurrency[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Search dialog state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (state.user?.id) {
      loadWatchlist();
    }
  }, [state.user]);

  const loadWatchlist = async () => {
    try {
      setLoading(true);
      const coins = await cryptoPriceService.getWatchlist(state.user?.id ?? '');
      setWatchlist(coins);
      await loadPrices(coins);
    } catch (error) {
      console.error('Error loading watchlist:', error);
      setError('Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  };

  const loadPrices = async (coins: string[]) => {
    try {
      if (coins.length === 0) {
        setCryptoData([]);
        return;
      }
      
      const data = await cryptoPriceService.getPrices(coins);
      setCryptoData(data);
    } catch (error) {
      console.error('Error loading crypto prices:', error);
      setError('Failed to load crypto prices');
    }
  };

  const refreshPrices = async () => {
    try {
      setRefreshing(true);
      await loadPrices(watchlist);
    } catch (error) {
      console.error('Error refreshing prices:', error);
      setError('Failed to refresh prices');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRemoveCoin = async (coinId: string) => {
    try {
      const updatedWatchlist = watchlist.filter(id => id !== coinId);
      setWatchlist(updatedWatchlist);
      setCryptoData(cryptoData.filter(crypto => crypto.id !== coinId));
      await cryptoPriceService.saveWatchlist(state.user?.id ?? '', updatedWatchlist);
    } catch (error) {
      console.error('Error removing coin:', error);
      setError('Failed to remove coin from watchlist');
    }
  };

  const handleAddCoin = async (coinId: string) => {
    try {
      if (watchlist.includes(coinId)) {
        return; // Already in watchlist
      }
      
      const updatedWatchlist = [...watchlist, coinId];
      setWatchlist(updatedWatchlist);
      await cryptoPriceService.saveWatchlist(state.user?.id ?? '', updatedWatchlist);
      
      // Load prices for the updated watchlist
      await loadPrices(updatedWatchlist);
      
      // Close the search dialog
      setSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
    } catch (error) {
      console.error('Error adding coin:', error);
      setError('Failed to add coin to watchlist');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      return;
    }
    
    try {
      setSearching(true);
      const results = await cryptoPriceService.searchCoins(searchQuery);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching coins:', error);
    } finally {
      setSearching(false);
    }
  };

  // Format currency values
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format percentage values
  const formatPercentage = (value: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      signDisplay: 'exceptZero'
    }).format(value / 100);
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Crypto Watchlist</Typography>
          <Box>
            <Button 
              startIcon={<AddIcon />} 
              onClick={() => setSearchOpen(true)}
              size="small"
              variant="outlined"
              sx={{ mr: 1 }}
            >
              Add
            </Button>
            <Button 
              startIcon={<RefreshIcon />} 
              onClick={refreshPrices}
              disabled={refreshing}
              size="small"
              variant="outlined"
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
        </Box>
        
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : cryptoData.length > 0 ? (
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Asset</TableCell>
                  <TableCell align="right">Price (USD)</TableCell>
                  <TableCell align="right">24h Change</TableCell>
                  <TableCell align="right">Market Cap</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cryptoData.map((crypto) => {
                  const priceChangeIsPositive = crypto.price_change_percentage_24h > 0;
                  
                  return (
                    <TableRow key={crypto.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <img 
                            src={crypto.image} 
                            alt={crypto.name} 
                            style={{ width: 24, height: 24, marginRight: 8 }} 
                          />
                          <Box>
                            <Typography variant="body2">
                              {crypto.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {crypto.symbol.toUpperCase()}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(crypto.current_price)}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          color: priceChangeIsPositive ? 'success.main' : 'error.main',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          {priceChangeIsPositive ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                          {formatPercentage(crypto.price_change_percentage_24h)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(crypto.market_cap)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Remove from watchlist">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleRemoveCoin(crypto.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No cryptocurrencies in your watchlist. Click "Add" to add some.
          </Typography>
        )}
      </CardContent>
      
      {/* Search Dialog */}
      <Dialog 
        open={searchOpen} 
        onClose={() => setSearchOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Cryptocurrency</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={10}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Search by name or symbol"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  size="small"
                  autoFocus
                />
              </Grid>
              <Grid item xs={2}>
                <Button 
                  fullWidth
                  variant="contained" 
                  onClick={handleSearch}
                  disabled={searching}
                  startIcon={<SearchIcon />}
                  sx={{ height: '40px' }}
                >
                  {searching ? <CircularProgress size={24} /> : 'Search'}
                </Button>
              </Grid>
            </Grid>
          </Box>
          
          <Divider sx={{ my: 1 }} />
          
          {searchResults.length > 0 ? (
            <List sx={{ maxHeight: 350, overflow: 'auto' }}>
              {searchResults.map((coin) => {
                const alreadyAdded = watchlist.includes(coin.id);
                
                return (
                  <ListItem key={coin.id} divider>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {coin.thumb && (
                        <img 
                          src={coin.thumb} 
                          alt={coin.name} 
                          style={{ width: 24, height: 24, marginRight: 8 }} 
                        />
                      )}
                      <ListItemText
                        primary={coin.name}
                        secondary={`${coin.symbol.toUpperCase()} • Market Cap Rank: ${coin.market_cap_rank || 'N/A'}`}
                      />
                    </Box>
                    <ListItemSecondaryAction>
                      {alreadyAdded ? (
                        <Chip label="Added" color="primary" size="small" />
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleAddCoin(coin.id)}
                        >
                          Add
                        </Button>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          ) : searchQuery && !searching ? (
            <Typography variant="body2" align="center" sx={{ py: 3 }}>
              No results found. Try a different search term.
            </Typography>
          ) : !searchQuery ? (
            <Typography variant="body2" align="center" sx={{ py: 3 }}>
              Enter a cryptocurrency name or symbol to search.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default CryptoWatchlist;
